/* eslint-disable @typescript-eslint/no-explicit-any */

import { createSubscriber } from 'svelte/reactivity';

import { TeoCore } from '@/teoCore/TeoCore';
import type { TextUi, TextUiState } from '@/teoCore/text/TextUi';
import type { TeoEventTarget } from '@/teoCore/utility/TeoEventTarget';

type ExtractEventKey<TEventTarget extends TeoEventTarget<any>> =
  TEventTarget extends TeoEventTarget<infer TEvents> ? keyof TEvents | keyof TEvents : never;

type ExtractUpdateCallback<
  T,
  TEventTarget extends TeoEventTarget<any>,
  TSelectedEvent extends ExtractEventKey<TEventTarget>,
> =
  TEventTarget extends TeoEventTarget<infer TEvents> ?
    {
      [K in TSelectedEvent]: (event: K, ...args: Parameters<TEvents[K]>) => T | Promise<T>;
    }[TSelectedEvent]
  : never;

export interface ObserverResult<T> {
  get current(): T;
  get value(): T;
}

// TODO: this is a userland implementation of createSubscriber.
export function useTeoEventObserver<
  T,
  TEventTarget extends TeoEventTarget<any>,
  TSelectedEvent extends ExtractEventKey<TEventTarget>,
>({
  initialValue,
  update,
  target,
  events,
  debug,
}: {
  initialValue: T;
  update: ExtractUpdateCallback<T, TEventTarget, TSelectedEvent>;
  target: TEventTarget;
  events: TSelectedEvent | TSelectedEvent[];
  debug?: string;
}): ObserverResult<T> {
  let value = $state(initialValue);

  $effect(() => {
    const eventsToListen = Array.isArray(events) ? events : [events];
    const unsubscribes = eventsToListen.map((event) =>
      target.addEventListener(event as any, async (...eventArgs: any[]) => {
        if (debug) {
          console.log(`${debug}@${String(event)}`, ...eventArgs);
        }
        value = await update(event as any, ...eventArgs);
      }),
    );
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  });

  return {
    get current() {
      return value;
    },

    get value() {
      return value;
    },
  };
}

export function useTeoTextUiStateObserver<K extends keyof TextUiState>(
  ui: TextUi,
  event: K,
): ObserverResult<TextUiState[K]> {
  return useTeoEventObserver({
    initialValue: ui.state[event],
    // Events arrive queued; reading the event's value would hand components stale values to echo back.
    update: () => ui.state[event],
    target: ui.events,
    events: event,
  } as any);
}

// export function useDynamicConfigOption<K extends keyof DynamicConfigOptions>(
//   config: DynamicConfig,
//   event: K,
// ): ObserverResult<DynamicConfigOptions[K]> {
//   return useTeoEventObserver({
//     initialValue: config.options[event],
//     update: () => {
//       return config.options[event];
//     },
//     target: config,
//     events: event,
//   });
// }

export function useActiveText(core?: () => TeoCore) {
  const tc = core ?? (() => TeoCore.instance);
  return useTeoEventObserver({
    initialValue: tc().activeText,
    update: () => tc().activeText,
    target: tc().events,
    events: ['activeTextChanged'],
  });
}

export class TeoActiveText {
  #core: TeoCore;
  #subscribe;

  constructor(core: TeoCore) {
    this.#core = core;
    this.#subscribe = createSubscriber((update) => {
      return this.#core.events.addEventListener('activeTextChanged', () => {
        update();
      });
    });
  }

  get current() {
    this.#subscribe();
    return this.#core.activeText;
  }
}
